import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mediaAbs } from '@/lib/hosts';
import { CELL_STORED_STORAGE_ORIGIN, isCell } from '@/lib/cell/config';
import { cellSign } from '@/lib/cell/files';

/**
 * PHOTOS IN THE TWO BUCKETS THAT GO PRIVATE — security fix #53 (Peter's go, 2026-09-25).
 *
 * `spotlight-photos` and `ops-photos` were PUBLIC buckets: anyone holding a
 * `/storage/v1/object/public/…` link could load the file, though the storage policies only
 * ever meant signed-in studio members to (`*_authed_select` → app_can_access_location on the
 * first folder, which resolves through pro_locations for a pro). Once the buckets are private a
 * public link returns 400, so every screen here that shows one of those photos asks for a
 * short-lived signed link with the pro's own session instead.
 *
 * WHAT IS LEFT ALONE: `/m/` addresses (R2, served by the Owner Hub) are the platform's durable
 * public media and are only re-rooted onto the media host, exactly as before. Any other absolute
 * address passes through untouched.
 *
 * THE OLD PROJECT HOST. Four `spotlights.photo_urls` entries still name the pre-migration
 * project (`mfrjfhtpiaopwitoeofz`). Every one of those objects exists at the same path in this
 * project's bucket (checked 2026-09-25: 4 of 4), so they are signed HERE rather than loaded
 * from a project that is being retired — which is also what keeps them alive when it goes.
 */

/** The buckets #53 makes private — the only ones this helper signs FOR DISPLAY. */
export const PRIVATE_PHOTO_BUCKETS = ['spotlight-photos', 'ops-photos'] as const;
export type PrivatePhotoBucket = (typeof PRIVATE_PHOTO_BUCKETS)[number];

/**
 * SIGN ON OPEN (e33f5778, AWSM-301). A pro's certificate (compliance_records.document_url) and a ticket's photos were
 * stored as a Supabase link signed for a YEAR, so every stored link dies the day storage leaves Supabase; on AWS the cell
 * caps a link at 7 days (tenean-infra api/src/routes/pro-hub/files.ts), so a link stored there dies within a week. These
 * buckets are therefore stored as an ADDRESS (storageAddress) and signed only when someone opens the file
 * (openStoredFile), as the owner web does (image-os-web /files/open, AWSM-103 #2161) and the phones do (#475/#308).
 */
export const SIGN_ON_OPEN_BUCKETS = ['compliance-docs', 'maintenance-photos'] as const;
export type SignOnOpenBucket = (typeof SIGN_ON_OPEN_BUCKETS)[number];
export type StoredBucket = PrivatePhotoBucket | SignOnOpenBucket;
/** How long an opened file's link lives: long enough to open it, never stored. */
export const OPEN_TTL_SECONDS = 5 * 60;

/** How long a display link lives. Long enough for a session on one screen; the screens re-sign on
 *  every mount or refetch, so nothing stores these links. */
export const DISPLAY_TTL_SECONDS = 6 * 60 * 60;

/**
 * This project's own address, read from the same build variable the client is created from
 * (src/lib/supabase.ts). Read here rather than imported as PROJECT_URL so the screens' tests,
 * which mock '@/lib/supabase' with only the client, need no extra export.
 */
// CELL (M28): the cell build has no Supabase URL; its stored-address origin is text only (lib/cell/config.ts).
const ownProjectUrl = (): string => (isCell() ? CELL_STORED_STORAGE_ORIGIN : String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, ''));

/** Any of Supabase's three object forms: public, signed, or token-authenticated. */
const STORAGE_ADDRESS = /^https?:\/\/([^/?#]+)\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/?#]+)\/([^?#]+)/i;

function isOurStorageHost(host: string): boolean {
  const h = host.toLowerCase();
  let own = '';
  try { own = ownProjectUrl() ? new URL(ownProjectUrl()).host.toLowerCase() : ''; } catch { own = ''; }
  // Ours, or a Supabase project host — the old project's addresses name the same paths (above).
  // CELL (M28): a host-name test, not a call — Supabase is never contacted here.
  return (!!own && h === own) || h.endsWith('.supabase.co');
}

/** The bucket and object path a stored address points at, for the given buckets — null when it is not one of ours. */
function objectIn<B extends string>(url: string | null | undefined, buckets: readonly B[]): { bucket: B; path: string } | null {
  const m = STORAGE_ADDRESS.exec(String(url ?? '').trim());
  if (!m) return null;
  const [, host, bucket, raw] = m;
  if (!(buckets as readonly string[]).includes(bucket) || !isOurStorageHost(host)) return null;
  let path = raw;
  try { path = decodeURIComponent(raw); } catch { /* a malformed escape: keep it as written */ }
  return path ? { bucket: bucket as B, path } : null;
}

/** The bucket and object path a stored PHOTO address points at — null when it is not one of ours. */
export function storageObjectOf(url: string | null | undefined): { bucket: PrivatePhotoBucket; path: string } | null {
  return objectIn(url, PRIVATE_PHOTO_BUCKETS);
}

/** The bucket and object path a stored certificate or ticket-photo address points at (any of the three forms, ours or a
 *  Supabase project host) — null when it is not one of ours, which then opens as it is. */
export function storedFileOf(url: string | null | undefined): { bucket: SignOnOpenBucket; path: string } | null {
  return objectIn(url, SIGN_ON_OPEN_BUCKETS);
}

/**
 * The address STORED for a new upload into one of these buckets: the token-authenticated object
 * form, which names project, bucket and path unambiguously and grants nothing on its own. Never
 * the `/object/public/` form — that stops loading the day the bucket goes private — and never a
 * signed link, whose expiry is a fuse (the 2026-08-18 lesson). Readers sign it for display.
 */
export function storageAddress(bucket: StoredBucket, path: string): string {
  const base = ownProjectUrl();
  // A build without the project address cannot talk to Supabase at all; storing a relative
  // address would resolve against whichever page later rendered it, so refuse instead.
  if (!/^https?:\/\//i.test(base)) throw new Error('This build has no storage address configured.');
  return `${base}/storage/v1/object/authenticated/${bucket}/${path.replace(/^\/+/, '')}`;
}

/** A signed link for one object with the caller's own session, or null when refused or failed. */
export async function signStoragePath(
  bucket: StoredBucket, path: string, ttl: number = DISPLAY_TTL_SECONDS,
): Promise<string | null> {
  try {
    // CELL (M28): POST /v1/pro-hub/files/sign — the bucket's storage rule run in the cell, a presigned S3 link back.
    const { data, error } = isCell()
      ? await cellSign(bucket, path, ttl)
      : await supabase.storage.from(bucket).createSignedUrl(path, ttl);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
}

/**
 * What to show if signing is refused. A stored PUBLIC address is returned as it is: until the
 * bucket flips it still loads, so a refusal is never worse than the screen was before. Anything
 * else (an authenticated address, a bare path) cannot load unsigned, so nothing is shown rather
 * than a broken image.
 */
function unsignedFallback(url: string): string | null {
  return /\/storage\/v1\/object\/public\//i.test(url) ? url : null;
}

/** The address a screen should put in an <img> for a stored photo address. */
export async function displayPhotoUrl(url: string | null | undefined): Promise<string | null> {
  if (!url) return null;
  const obj = storageObjectOf(url);
  if (!obj) return mediaAbs(url);
  return (await signStoragePath(obj.bucket, obj.path)) ?? unsignedFallback(url);
}

/**
 * One signing hook for both entry points. `key` identifies what is being shown; the result is
 * null while the link is being minted, so no dead image flashes before it arrives.
 */
function useSigned(key: string | null, sign: () => Promise<string | null>): string | null {
  const [signed, setSigned] = useState<{ for: string; url: string | null } | null>(null);
  useEffect(() => {
    if (!key) return;
    let live = true;
    void sign().then((u) => { if (live) setSigned({ for: key, url: u }); });
    return () => { live = false; };
    // `sign` is rebuilt every render from the same inputs `key` names, so key is the dependency.
  }, [key]);
  return key && signed?.for === key ? signed.url : null;
}

/** Hook form of displayPhotoUrl: `/m/` and foreign addresses immediately, ours once signed. */
export function useDisplayPhotoUrl(url: string | null | undefined): string | null {
  const obj = url ? storageObjectOf(url) : null;
  const signed = useSigned(obj ? `addr:${url}` : null, () => displayPhotoUrl(url));
  if (!url) return null;
  return obj ? signed : mediaAbs(url);
}

/** A private object the caller holds only a PATH for (e.g. `pros.photo_path` in ops-photos).
 *  There is no public fallback: a refusal shows the screen's empty state, never a dead link. */
export function useSignedStoragePath(bucket: PrivatePhotoBucket, path: string | null | undefined): string | null {
  return useSigned(path ? `path:${bucket}/${path}` : null, () => signStoragePath(bucket, path as string));
}

/**
 * Open a stored certificate or ticket photo (e33f5778): ours → a five-minute link signed now with the caller's own session
 * (AWS: /v1/pro-hub/files/sign runs the bucket's rule, the uploader's clause included; production: Storage), in a tab
 * opened on the click itself so no popup blocker stops it; anything else opens as it is. The stored address's own token,
 * if any, is never used or shown. false when nothing could be opened.
 */
export async function openStoredFile(
  url: string, open: (u: string) => Window | null = (u) => window.open(u, '_blank'),
): Promise<boolean> {
  const obj = storedFileOf(url);
  if (!obj) { const w = open(url); if (w) w.opener = null; return !!w; }
  const w = open('about:blank');
  if (w) w.opener = null;
  const link = await signStoragePath(obj.bucket, obj.path, OPEN_TTL_SECONDS);
  if (!link) { w?.close(); return false; }
  if (w) w.location.href = link; else open(link);
  return true;
}
